import { DEFAULT_DEPARTURE_CITY_ID } from '@/entities/departureCity/@/searchCriteria';
import { getSearchHotelCategoryIds } from '@/entities/hotelCategory/@/searchCriteria';
import { getSearchMealIds } from '@/entities/meal/@/searchCriteria';
import { getCountryResorts, findHotels } from '@/shared/api';
import { useTravelataAppConfig } from '@/shared/composables';
import {
  type Country,
  type Hotel,
  type Resort,
  type SearchFormData,
  type SearchCriteriaParams,
  type DateRange,
  type NightsRange,
  type DepartureCity,
} from '@/shared/model';
import { correctDateRange } from './dateRange';
import { resolveDefaultCriteria } from './defaultCriteria';
import { toTouristGroup, toTouristRooms } from './touristRooms';
import { DEFAULT_TOURISTS } from '../config/tourists';
import { type SearchCriteria, type Selection, type TouristsSelection } from '../model/types';

export async function buildSearchCriteria(
  storageParams: SearchCriteriaParams | null,
  formData: SearchFormData,
  resortsUrl?: string,
  urlParams: Partial<SearchCriteriaParams> | null = null,
): Promise<SearchCriteria> {
  const params: SearchCriteriaParams | null = urlParams
    ? {
        checkInDateRange: { from: '', to: '' },
        countries: [],
        departureCity: { id: 0, name: '' },
        nightRange: { from: 0, to: 0 },
        touristGroup: { adults: 0, kids: 0, infants: 0, ages: [] },
        ...urlParams,
      }
    : storageParams;
  const { cities, countries } = formData;
  let destination: Selection | null = null;
  let dateRange: DateRange | null = null;
  let nights: NightsRange | null = null;
  let tourists: TouristsSelection | null = null;
  const appConfig = useTravelataAppConfig();

  if (params?.checkInDateRange.from && params.checkInDateRange.to) {
    dateRange = correctDateRange({
      daysFlexible: 0,
      ...params.checkInDateRange,
    });
  }
  if (params?.nightRange.from || params?.nightRange.to) {
    nights = {
      ...params.nightRange,
    };
  }
  if (params?.touristGroup.adults) {
    tourists = { rooms: toTouristRooms(params.touristGroup) };
  }

  const country = countries.find((c) => c.id === params?.countries[0]) || countries[0];
  const city =
    cities.find((c) => c.id === params?.departureCity.id) ||
    cities.find((c) => c.id === appConfig.defaultDepartureCityId) ||
    cities.find((c) => c.id === DEFAULT_DEPARTURE_CITY_ID);

  const fromCity: DepartureCity | null = city || null;
  const resorts = await getCountryResorts(country, resortsUrl);
  const defaultCriteria = resolveDefaultCriteria(formData.defaultCriteria, country.id, fromCity?.type);
  if (!dateRange) {
    dateRange = defaultCriteria.dateRange || null;
  }
  if (!nights) {
    nights = defaultCriteria.nights || null;
  }
  if (!tourists) {
    tourists = DEFAULT_TOURISTS;
  }
  if (params?.resorts) {
    destination = {
      country: { ...country, resorts },
      resorts: resorts.filter((r) => params.resorts?.includes(r.id)),
      kind: 'resorts',
    };
  } else if (params?.hotels) {
    const hotels = await findHotels({ country: country.id, hotels: params.hotels });
    destination = {
      kind: 'hotels',
      hotels,
      country: { ...country, resorts },
    };
  } else {
    destination = {
      kind: 'country',
      country: { ...country, resorts },
    };
  }

  return {
    dateRange,
    destination,
    tourists,
    nights,
    fromCity,
    ...getUrlOnlyCriteria(urlParams),
  };
}

function getUrlOnlyCriteria(
  urlParams: Partial<SearchCriteriaParams> | null,
): Pick<SearchCriteria, 'meals' | 'hotelCategories' | 'directFlight' | 'block'> {
  return {
    ...(urlParams?.meals?.length && { meals: urlParams.meals }),
    ...(urlParams?.hotelCategories?.length && { hotelCategories: urlParams.hotelCategories }),
    ...(urlParams?.directFlight && { directFlight: true }),
    ...(urlParams?.block && { block: true }),
  };
}

export function buildSearchCriteriaParams(params: SearchCriteria): SearchCriteriaParams {
  const countries: Country[] = [];
  const resorts: Resort[] = [];
  const hotels: Hotel[] = [];
  const { destination } = params;

  if (destination?.kind !== 'none' && destination?.country) {
    countries.push(destination?.country);
  }
  if (destination?.kind === 'resorts') {
    resorts.push(...destination.resorts);
  }
  if (destination?.kind === 'hotels') {
    hotels.push(...destination.hotels);
  }

  const meals = params.meals?.length ? getSearchMealIds(params.meals) : undefined;
  const hotelCategories = params.hotelCategories?.length
    ? getSearchHotelCategoryIds(params.hotelCategories)
    : undefined;

  return {
    checkInDateRange: {
      from: params.dateRange?.from || '',
      to: params.dateRange?.to || '',
    },
    nightRange: {
      from: params.nights?.from || 0,
      to: params.nights?.to || 0,
    },
    departureCity: {
      id: params.fromCity?.id || 0,
      name: params.fromCity?.name || '',
    },
    touristGroup: toTouristGroup(params.tourists?.rooms),
    countries: countries.map(({ id }) => id),
    resorts: resorts.map(({ id }) => id),
    hotels: hotels.map(({ id }) => id),
    directFlight: !!params?.directFlight,
    ...(params.block && { block: true }),
    ...(meals && { meals }),
    ...(hotelCategories && { hotelCategories }),
  };
}
export function buildHistoryCriteriaParams(params: SearchCriteria): SearchCriteriaParams {
  const historyParams = buildSearchCriteriaParams(params);

  if (!historyParams.resorts?.length) {
    delete historyParams.resorts;
  }
  if (!historyParams.hotels?.length) {
    delete historyParams.hotels;
  }

  delete historyParams.meals;
  delete historyParams.hotelCategories;
  delete historyParams.block;

  const daysFlexible = params.dateRange?.daysFlexible;
  if (daysFlexible) {
    historyParams.checkInDateRange = { ...historyParams.checkInDateRange, daysFlexible };
  }

  return historyParams;
}

export function buildStorageCriteriaParams(params: SearchCriteria): SearchCriteriaParams {
  const storageParams = buildHistoryCriteriaParams(params);

  delete storageParams.hotels;

  return storageParams;
}
